import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, AlertCircle, Plus, Trash2, Heart, Gift, ShoppingBag } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: "paypal", label: "PayPal" },
  { id: "cash_pickup", label: "Cash on Pickup" },
  { id: "bimpay", label: "BimPay" },
  { id: "firstpay", label: "FirstPay (CIBC Caribbean)" },
  { id: "other", label: "Other / Bank Transfer" }
];

const DELIVERY_METHODS = [
  { id: "pickup", label: "Pickup", price: 0 },
  { id: "delivery", label: "Delivery (Barbados)", price: 2 },
  { id: "rural", label: "Rural Delivery", price: 5 },
  { id: "manual", label: "Other / In-person", price: 0 }
];

const ORDER_TYPES = [
  { id: "sale", label: "Sale", icon: ShoppingBag, color: "#2A9D8F", helper: "Regular paid order — customer pays the total." },
  { id: "donation", label: "Donation", icon: Heart, color: "#E63946", helper: "Given to a charity / event / cause. Total forced to $0, inventory still deducts." },
  { id: "giveaway", label: "Giveaway", icon: Gift, color: "#E9C46A", helper: "Free promo copy (review, book club, personal gift). Total forced to $0, inventory still deducts." }
];

const emptyItem = () => ({ sku: "detective_diaries", unit_price: 15.99, quantity: 1 });

const ManualOrderPage = () => {
  const navigate = useNavigate();
  const { orderNumber } = useParams();
  const isEdit = Boolean(orderNumber);
  const [catalog, setCatalog] = useState([]);
  const [inventory, setInventory] = useState({}); // sku -> {current_stock, starting_stock}
  const [orderType, setOrderType] = useState("sale");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([emptyItem()]);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    delivery_method: "pickup",
    shipping: 0,
    payment_method: "cash_pickup",
    address_line1: "",
    city: "",
    country: "Barbados",
    notes: ""
  });

  useEffect(() => {
    axios.get(`${API}/api/catalog`).then((res) => setCatalog(res.data.items || []));
    // Inventory is admin-only — safe to fetch here because ManualOrderPage sits behind ProtectedRoute
    axios.get(`${API}/api/admin/inventory`, { withCredentials: true })
      .then((res) => {
        const map = {};
        (res.data.items || []).forEach((it) => { map[it.sku] = it; });
        setInventory(map);
      })
      .catch(() => { /* non-fatal — picker will just omit stock counts */ });
  }, []);

  // Load existing order when editing
  useEffect(() => {
    if (!isEdit) return;
    axios
      .get(`${API}/api/admin/orders/${orderNumber}`, { withCredentials: true })
      .then((res) => {
        const o = res.data;
        setOrderType(o.order_type || "sale");
        setForm({
          customer_name: o.customer_name || "",
          email: o.email || "",
          phone: o.phone || "",
          delivery_method: o.delivery_method || "pickup",
          shipping: o.shipping ?? 0,
          payment_method: o.payment_method || "cash_pickup",
          address_line1: o.address?.line1 || "",
          city: o.address?.city || "",
          country: o.address?.country || "Barbados",
          notes: o.notes || ""
        });
        const loaded = (o.items && o.items.length > 0)
          ? o.items.map((it) => ({ sku: it.sku, unit_price: it.unit_price, quantity: it.quantity }))
          : [emptyItem()];
        setItems(loaded);
      })
      .catch(() => setError("Failed to load order for editing."));
  }, [isEdit, orderNumber]);

  const catalogBySku = Object.fromEntries(catalog.map((c) => [c.sku, c]));

  const isFreebie = orderType === "donation" || orderType === "giveaway";

  const subtotal = items.reduce(
    (sum, it) => sum + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity, 10) || 0),
    0
  );
  const total = isFreebie ? 0 : subtotal + (parseFloat(form.shipping) || 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeliveryChange = (e) => {
    const id = e.target.value;
    const opt = DELIVERY_METHODS.find((d) => d.id === id);
    setForm((prev) => ({ ...prev, delivery_method: id, shipping: opt?.price ?? 0 }));
  };

  const handleItemChange = (idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      // Auto-fill price when SKU changes
      if (field === "sku") {
        const cat = catalogBySku[value];
        if (cat) next[idx].unit_price = cat.price;
      }
      return next;
    });
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const grouped = catalog.reduce((acc, item) => {
    (acc[item.category] = acc[item.category] || []).push(item);
    return acc;
  }, {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.customer_name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    if (items.length === 0) {
      setError("Add at least one item.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        address: {
          line1: form.address_line1.trim() || "",
          line2: "",
          city: form.city.trim() || "",
          state_province: "",
          postal_code: "",
          country: form.country.trim() || ""
        },
        items: items.map((it) => ({
          sku: it.sku,
          unit_price: parseFloat(it.unit_price) || 0,
          quantity: parseInt(it.quantity, 10) || 1
        })),
        shipping: isFreebie ? 0 : (parseFloat(form.shipping) || 0),
        delivery_method: form.delivery_method,
        payment_method: isFreebie ? null : form.payment_method,
        order_type: orderType,
        notes: form.notes || null
      };

      if (isEdit) {
        await axios.put(`${API}/api/admin/orders/${orderNumber}`, payload, { withCredentials: true });
        navigate(`/admin/invoice/${orderNumber}`);
      } else {
        // POST endpoint uses flat address_line1/city/... fields
        const createPayload = {
          ...payload,
          address_line1: form.address_line1.trim() || "",
          address_line2: "",
          city: form.city.trim() || "",
          state_province: "",
          postal_code: "",
          country: form.country.trim() || "",
          shipping_override: isFreebie ? 0 : (parseFloat(form.shipping) || 0),
          order_source: "manual"
        };
        delete createPayload.address;
        delete createPayload.shipping;
        const { data } = await axios.post(`${API}/api/admin/orders`, createPayload, { withCredentials: true });
        navigate(`/admin/invoice/${data.order_number}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isEdit ? "update" : "create"} order.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        <Link to="/admin/orders" className="inline-flex items-center gap-2 text-[#1D3557] font-bold mb-4 hover:text-[#E63946]" data-testid="back-orders-link">
          <ArrowLeft className="w-5 h-5" /> Back to Orders
        </Link>

        <h1 className="text-3xl font-black text-[#1D3557] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }} data-testid="manual-order-heading">
          {isEdit ? `Edit Order ${orderNumber}` : "New Manual Order"}
        </h1>
        <p className="text-[#1D3557]/80 font-medium mb-6">
          {isEdit
            ? "Update items, customer info, or status. Changes are saved immediately and reflected on the invoice."
            : "For in-person, phone, or WhatsApp orders. Add as many items as you like — book, merch, bundles, apparel."}
        </p>

        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="tactile-card p-6 space-y-6"
          data-testid="manual-order-form"
        >
          {/* Order Type — Sale / Donation / Giveaway */}
          <div>
            <label className="block font-bold text-[#1D3557] mb-2">Order Type</label>
            <div className="grid grid-cols-3 gap-2" data-testid="order-type-selector">
              {ORDER_TYPES.map((t) => {
                const Icon = t.icon;
                const active = orderType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setOrderType(t.id)}
                    className={`p-3 rounded-xl border-3 font-bold text-sm inline-flex flex-col items-center gap-1 transition-all ${
                      active ? "text-white shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]" : "bg-white text-[#1D3557] hover:shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]"
                    }`}
                    style={{
                      borderColor: "#1D3557",
                      backgroundColor: active ? t.color : undefined
                    }}
                    data-testid={`order-type-${t.id}`}
                  >
                    <Icon className="w-5 h-5" />
                    {t.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-[#1D3557]/70 font-medium mt-2 italic">
              {ORDER_TYPES.find((t) => t.id === orderType)?.helper}
            </p>
          </div>

          {/* Customer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block font-bold text-[#1D3557] mb-1">Customer Name *</label>
              <input name="customer_name" value={form.customer_name} onChange={handleChange} required className="input-field" data-testid="customer-name-input" />
            </div>
            <div>
              <label className="block font-bold text-[#1D3557] mb-1">Email *</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required className="input-field" data-testid="email-input" />
            </div>
            <div>
              <label className="block font-bold text-[#1D3557] mb-1">Phone</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} className="input-field" data-testid="phone-input" />
            </div>
          </div>

          {/* Address (optional) */}
          <details className="border-2 border-[#1D3557]/15 rounded-xl" open={isEdit && (form.address_line1 || form.city)} data-testid="address-details">
            <summary className="cursor-pointer font-bold text-[#1D3557] px-4 py-3 hover:bg-[#E9C46A]/10 rounded-xl select-none" data-testid="address-toggle">
              Address (optional, for invoice &ldquo;Bill To&rdquo;)
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              <div className="md:col-span-2">
                <label className="block font-bold text-[#1D3557] mb-1">Address Line 1</label>
                <input name="address_line1" value={form.address_line1} onChange={handleChange} className="input-field" data-testid="address-input" />
              </div>
              <div>
                <label className="block font-bold text-[#1D3557] mb-1">City / Parish</label>
                <input name="city" value={form.city} onChange={handleChange} className="input-field" />
              </div>
              <div>
                <label className="block font-bold text-[#1D3557] mb-1">Country</label>
                <input name="country" value={form.country} onChange={handleChange} className="input-field" />
              </div>
            </div>
          </details>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-[#1D3557] text-lg" style={{ fontFamily: "'Fredoka', sans-serif" }}>Items</h2>
              <button
                type="button"
                onClick={addItem}
                className="bg-[#2A9D8F] hover:bg-[#1D3557] text-white font-bold text-sm px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
                data-testid="add-item-btn"
              >
                <Plus className="w-4 h-4" /> Add item
              </button>
            </div>

            <div className="space-y-3">
              {items.map((it, idx) => {
                const stockInfo = inventory[it.sku];
                const currentStock = stockInfo?.current_stock ?? null;
                const requestedQty = parseInt(it.quantity, 10) || 0;
                const oversold = currentStock !== null && requestedQty > currentStock;
                return (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-[#FDFBF7] border-2 border-[#1D3557]/15 rounded-xl" data-testid={`item-row-${idx}`}>
                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-xs font-bold text-[#1D3557]/70 mb-1 uppercase tracking-wider">Item</label>
                    <select
                      value={it.sku}
                      onChange={(e) => handleItemChange(idx, "sku", e.target.value)}
                      className="select-field text-sm"
                      data-testid={`item-sku-${idx}`}
                    >
                      {Object.entries(grouped).map(([category, list]) => (
                        <optgroup key={category} label={category}>
                          {list.map((c) => {
                            const inv = inventory[c.sku];
                            const stockLabel = inv ? ` · ${inv.current_stock} left` : "";
                            return (
                              <option key={c.sku} value={c.sku}>
                                {c.name} — ${c.price.toFixed(2)}{stockLabel}
                              </option>
                            );
                          })}
                        </optgroup>
                      ))}
                    </select>
                    {currentStock !== null && (
                      <p className={`text-xs font-bold mt-1 ${oversold ? "text-[#E63946]" : "text-[#1D3557]/60"}`} data-testid={`item-stock-hint-${idx}`}>
                        Stock on hand: {currentStock}{oversold ? ` — you're ordering ${requestedQty}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="block text-xs font-bold text-[#1D3557]/70 mb-1 uppercase tracking-wider">Qty</label>
                    <input
                      type="number"
                      min="1"
                      value={it.quantity}
                      onChange={(e) => handleItemChange(idx, "quantity", e.target.value)}
                      className="input-field text-sm"
                      data-testid={`item-qty-${idx}`}
                    />
                  </div>
                  <div className="col-span-5 md:col-span-3">
                    <label className="block text-xs font-bold text-[#1D3557]/70 mb-1 uppercase tracking-wider">Unit $ (override)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={it.unit_price}
                      onChange={(e) => handleItemChange(idx, "unit_price", e.target.value)}
                      className="input-field text-sm"
                      data-testid={`item-price-${idx}`}
                    />
                  </div>
                  <div className="col-span-3 md:col-span-1 flex justify-end">
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="text-[#E63946] hover:bg-[#E63946]/10 p-2 rounded-lg"
                        aria-label="Remove"
                        data-testid={`item-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Delivery & Payment */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {!isFreebie && (
              <>
                <div>
                  <label className="block font-bold text-[#1D3557] mb-1">Delivery Method</label>
                  <select name="delivery_method" value={form.delivery_method} onChange={handleDeliveryChange} className="select-field" data-testid="delivery-select">
                    {DELIVERY_METHODS.map((d) => (
                      <option key={d.id} value={d.id}>{d.label}{d.price > 0 ? ` (+$${d.price})` : ""}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-[#1D3557] mb-1">Shipping ($)</label>
                  <input type="number" step="0.01" min="0" name="shipping" value={form.shipping} onChange={handleChange} className="input-field" data-testid="shipping-input" />
                </div>
                <div>
                  <label className="block font-bold text-[#1D3557] mb-1">Payment Method</label>
                  <select name="payment_method" value={form.payment_method} onChange={handleChange} className="select-field" data-testid="payment-select">
                    {PAYMENT_METHODS.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {isFreebie && (
              <div className="md:col-span-3 bg-[#E9C46A]/20 rounded-xl p-3 border-2 border-[#E9C46A] flex items-center gap-2" data-testid="freebie-notice">
                <AlertCircle className="w-5 h-5 text-[#E63946] flex-shrink-0" />
                <p className="text-xs text-[#1D3557] font-bold">
                  {orderType === "donation" ? "Donation" : "Giveaway"} — total forced to $0, no payment collected, no delivery fee. Inventory still deducts.
                </p>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block font-bold text-[#1D3557] mb-1">Notes (optional)</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows="2" className="input-field" placeholder="e.g. paid in cash at school fair" data-testid="notes-input" />
          </div>

          {/* Totals */}
          <div className="bg-[#E9C46A]/20 rounded-xl p-4 border-2 border-[#E9C46A]">
            <div className="flex justify-between text-[#1D3557] font-medium text-sm">
              <span>Items subtotal</span>
              <span className={isFreebie ? "line-through opacity-60" : ""}>${subtotal.toFixed(2)}</span>
            </div>
            {!isFreebie && (
              <div className="flex justify-between text-[#1D3557] font-medium text-sm">
                <span>Shipping</span>
                <span>${(parseFloat(form.shipping) || 0).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-[#1D3557] font-black text-lg border-t-2 border-[#1D3557]/15 mt-2 pt-2">
              <span>{isFreebie ? `Total (${orderType})` : "Total"}</span>
              <span className="text-[#E63946]" data-testid="order-total">${total.toFixed(2)} USD</span>
            </div>
          </div>

          {error && (
            <div className="bg-[#E63946]/10 border-2 border-[#E63946] rounded-xl p-3 flex items-center gap-2" data-testid="error">
              <AlertCircle className="w-5 h-5 text-[#E63946]" />
              <p className="text-[#E63946] font-medium">{error}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={loading} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="submit-btn">
              <FileText className="w-5 h-5" />
              {loading ? "Saving…" : (isEdit ? "Save changes & open invoice" : "Create order & open invoice")}
            </button>
            <Link to="/admin/orders" className="btn-secondary inline-flex items-center gap-2" data-testid="cancel-btn">
              Cancel
            </Link>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default ManualOrderPage;
